import { useMemo } from 'react';
import { createUseStyles } from 'react-jss';
import { Order, OrderStatus, useCancelOrder, useCompleteOrder } from '@app/api';
import { Colors, Theme } from '@app/theme';
import { OrderPriceEdition, ButtonIcon, Chip } from 'components';
import classnames from 'classnames';

const useStyles = createUseStyles<string, {}, Theme>((theme: Theme) => ({
  statusLine: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: theme.marginBase,
  },
  disabled: {
    opacity: 0.5,
  },
  container: {
    borderBottom: {
      color: theme.colors.grayBorder,
      width: 1,
      style: 'solid',
    },
    justifyContent: 'space-between',
    padding: [[theme.marginBase, 0]],
  },
  names: {
    ...theme.fonts.caption,
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    textWrap: 'nowrap',
  },
  buttons: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.marginBase / 2,
  },
  content: {
    backgroundColor: theme.colors.grayBackground,
    borderRadius: theme.borderRadius.std,
    marginTop: theme.marginBase,
    padding: theme.marginBase,
  },
}));

const statusColors: Record<OrderStatus, Colors> = {
  [OrderStatus.DRAFT]: 'inkSecondary',
  [OrderStatus.PENDING]: 'yellow',
  [OrderStatus.COMPLETED]: 'green',
  [OrderStatus.CANCELLED]: 'red',
  [OrderStatus.BILLING_IN_PROGRESS]: 'green',
  [OrderStatus.BILLED]: 'green',
};

interface Props {
  order: Order;
}

export const EditableOrder = ({ order }: Props) => {
  const classes = useStyles();

  const { mutate: completeOrder } = useCompleteOrder(order.id, order.sessionId);
  const { mutate: cancelOrder } = useCancelOrder(order.id, order.sessionId);

  const productNames = useMemo(() => {
    if (!order?.productLines) {
      return [];
    }
    return order.productLines
      .map((line) => {
        if (!line.product?.title) {
          return undefined;
        }
        return line.product.title;
      })
      .filter(Boolean);
  }, [order]);

  const context = useMemo(
    () => ({
      sessionId: order.sessionId,
      planId: order.planId,
      organizationId: order.organizationId,
    }),
    [order],
  );

  return (
    <div className={classes.container}>
      <div className={classes.statusLine}>
        <div
          className={classnames(classes.names, {
            [classes.disabled]: order.status === OrderStatus.CANCELLED,
          })}
        >
          {productNames.join(', ') || '(vide)'}
        </div>
        <div className={classes.buttons}>
          <Chip color={statusColors[order.status]}>{order.status}</Chip>
          {(order.status === OrderStatus.COMPLETED ||
            order.status === OrderStatus.PENDING) && (
            <ButtonIcon
              icon="ri-close-line"
              color="red"
              onClick={() => cancelOrder(context)}
              title="Cancel"
            />
          )}
          {order.status === OrderStatus.PENDING && (
            <ButtonIcon
              icon="ri-check-line"
              color="green"
              onClick={() => completeOrder(context)}
              title="Complete"
            />
          )}
          {order.status === OrderStatus.CANCELLED && (
            <ButtonIcon
              icon="ri-arrow-go-back-line"
              color="green"
              onClick={() => completeOrder(context)}
              title="Complete"
            />
          )}
        </div>
      </div>
      {order.status !== OrderStatus.CANCELLED && (
        <div className={classes.content}>
          <OrderPriceEdition order={order} />
        </div>
      )}
    </div>
  );
};
